import React, { useEffect, useState } from "react";
import { PostHog } from "posthog-js";
import { PostHogProvider as PostHogJSProvider } from "posthog-js/react";

let posthogInstance: PostHog | null = null;
let initializationPromise: Promise<PostHog | null> | null = null;

// Initialize PostHog client-side only
export function getPostHogClient(): PostHog | null {
  if (typeof window === "undefined") return null;

  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  if (!posthogKey) {
    console.log("🔍 No PostHog key found in VITE_PUBLIC_POSTHOG_KEY");
    return null;
  }

  // console.log('🔍 PostHog key found:', posthogKey.substring(0, 10) + '...');

  if (!posthogInstance) {
    try {
      posthogInstance = new PostHog();

      posthogInstance.init(posthogKey, {
        api_host:
          import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
          "https://us.i.posthog.com",
        person_profiles: "identified_only",
        capture_pageview: false, // We'll capture manually
        capture_pageleave: true,
        cross_subdomain_cookie: false,
        secure_cookie: import.meta.env.VITE_PUBLIC_ENV === "production",
        // Add debugging options
        debug: import.meta.env.VITE_PUBLIC_ENV === "development",
        loaded: (posthog) => {
          console.log("✅ PostHog loaded successfully");
          // Force feature flags to load
          posthog.reloadFeatureFlags();
        },
        // Feature flag options
        bootstrap: {
          featureFlags: {}, // Start with empty, will be populated
        },
      });

      // console.log('✅ PostHog client initialized');
    } catch (error) {
      console.error("❌ Failed to initialize PostHog:", error);
      posthogInstance = null;
    }
  }

  return posthogInstance;
}

// Async version that ensures PostHog is fully loaded with feature flags
export async function getPostHogClientAsync(): Promise<PostHog | null> {
  if (typeof window === "undefined") return null;

  const client = getPostHogClient();
  if (!client) return null;

  // Wait for feature flags to load if they haven't already
  if (!initializationPromise) {
    initializationPromise = new Promise((resolve) => {
      if (client.isFeatureEnabled("feature_email") !== undefined) {
        // Feature flags are already available
        // console.log('🔍 Feature flags already loaded');
        resolve(client);
      } else {
        // console.log("🔍 Waiting for feature flags to load...");
        // Wait for feature flags to load
        const unsubscribe = client.onFeatureFlags(() => {
          // console.log("🔍 Feature flags loaded via callback");
          unsubscribe?.();
          resolve(client);
        });

        // Force reload feature flags
        client.reloadFeatureFlags();

        // Timeout after 5 seconds
        setTimeout(() => {
          console.log("🔍 Feature flags timeout reached, proceeding anyway");
          unsubscribe?.();
          resolve(client);
        }, 5000);
      }
    });
  }

  return initializationPromise;
}

// PostHog Provider Component
export function PostHogClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [client, setClient] = useState<PostHog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function initializeClient() {
      try {
        const posthog = await getPostHogClientAsync();
        setClient(posthog);
      } catch (error) {
        console.error("Error initializing PostHog client:", error);
        setClient(null);
      } finally {
        setLoading(false);
      }
    }

    initializeClient();
  }, []);

  if (loading) {
    return <>{children}</>;
  }

  if (!client) {
    return <>{children}</>;
  }

  return <PostHogJSProvider client={client}>{children}</PostHogJSProvider>;
}

// Simple wrapper for compatibility
export const PostHogProvider = PostHogClientProvider;

// User identification component - to be used in a separate file
export function createPostHogUserIdentifier(useAuth: any) {
  return function PostHogUserIdentifier() {
    const { user, userData, loading } = useAuth();

    useEffect(() => {
      async function identifyUser() {
        const posthog = await getPostHogClientAsync();
        if (!posthog) return;

        if (loading) {
          return; // Wait for auth to load
        }

        if (user && userData && userData.tenantId) {
          // Identify user with PostHog
          const userProperties = {
            email: user.email,
            displayName: userData.displayName,
            tenantId: userData.tenantId,
            role: userData.role,
            permissions: userData.permissions,
          };

          posthog.identify(user.uid, userProperties);

          // Set user groups for role-based feature flags
          if (userData.role) {
            posthog.group("role", userData.role);
          }

          if (userData.tenantId) {
            posthog.group("tenant", userData.tenantId);
          }

          // Track login event
          posthog.capture("user_logged_in", {
            method: "session_restored",
            tenantId: userData.tenantId,
            role: userData.role,
          });

          console.log(
            "✅ User identified with PostHog:",
            user.email,
            userData.role,
          );
        } else if (!loading && !user) {
          // Reset user when logged out
          console.log("🔄 Resetting PostHog user");
          posthog.reset();
        }
      }

      identifyUser();
    }, [user, userData, loading]);

    return null;
  };
}

// Hook for using PostHog
export const usePostHog = () => {
  const [posthog, setPosthog] = useState<PostHog | null>(null);

  useEffect(() => {
    getPostHogClientAsync().then(setPosthog);
  }, []);

  return posthog;
};

// Hook for tracking events
export function usePostHogTracking() {
  const [posthog, setPosthog] = useState<PostHog | null>(null);

  useEffect(() => {
    getPostHogClientAsync().then(setPosthog);
  }, []);

  const track = (event: string, properties?: Record<string, any>) => {
    if (!posthog) return;
    posthog.capture(event, properties);
  };

  const identify = (userId: string, properties?: Record<string, any>) => {
    if (!posthog) return;
    posthog.identify(userId, properties);
  };

  return { track, identify };
}

// Page view tracking
export function usePostHogPageView() {
  useEffect(() => {
    async function trackPageView() {
      const posthog = await getPostHogClientAsync();
      if (!posthog) return;
      posthog.capture("$pageview");
    }

    trackPageView();
  }, []);
}
